import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, ApiError } from '../api.ts'
import { money } from '../format.ts'

interface Item {
  id: string
  model: string
  manufacturerName: string
  catalogCode: string
  unitNetPrice: string
  promisedDeliveryDate: string | null
  cancelledAt: string | null
  cancelReason: string | null
  supplierOrderId: string | null
  supplierNumber: string | null
  supplierStatus: string | null
  confirmedDeliveryDate: string | null
}
interface Order {
  id: string
  number: string
  customerName: string
  customerCity: string | null
  salesCode: string | null
  sourceQuoteId: string | null
  deliveryAddress: string | null
  status: string
  deliveryAtRisk: boolean
  items: Item[]
}

/** One customer order (ZK): its machines, and the button that orders them. */
export function OrderPage() {
  const { id } = useParams<{ id: string }>()
  const [o, setO] = useState<Order | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [flash, setFlash] = useState<string | null>(null)

  function reload() {
    if (!id) return
    api
      .get<{ order: Order }>(`/api/customer-orders/${id}`)
      .then((r) => setO(r.order))
      .catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
  }
  useEffect(reload, [id])

  async function orderFromSuppliers() {
    if (!o) return
    setBusy(true)
    setError(null)
    setFlash(null)
    try {
      const r = await api.post<{ created: { number: string; manufacturerName: string }[] }>(
        `/api/customer-orders/${o.id}/supplier-orders`,
      )
      setFlash(`Utworzono zamówienia: ${r.created.map((z) => `${z.number} (${z.manufacturerName})`).join(', ')}`)
      reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    } finally {
      setBusy(false)
    }
  }

  async function cancelItem(itemId: string) {
    const reason = window.prompt('Powód rezygnacji klienta z tej maszyny?')
    if (!reason?.trim()) return
    try {
      await api.post(`/api/customer-order-items/${itemId}/cancel`, { reason: reason.trim() })
      reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    }
  }

  if (error && !o) return <div className="p-6 text-red-700">{error}</div>
  if (!o) return <div className="p-6 text-slate-400">Ładowanie…</div>

  const doZamowienia = o.items.filter((i) => !i.cancelledAt && !i.supplierOrderId)

  async function setPromised(itemId: string, date: string | null) {
    setError(null)
    try {
      await api.patch(`/api/customer-order-items/${itemId}`, { promisedDeliveryDate: date })
      reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się zapisać terminu')
    }
  }

  return (
    <div className="p-6 max-w-4xl">
      <Link to="/zamowienia" className="text-sm text-slate-500 hover:text-slate-700">
        ← Zamówienia
      </Link>

      <div className="mt-2 mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">{o.number}</h1>
          <div className="text-sm text-slate-500 mt-0.5">
            {o.customerName}
            {o.customerCity && ` · ${o.customerCity}`}
            {o.salesCode && <span className="text-slate-400"> · {o.salesCode}</span>}
          </div>
          {o.sourceQuoteId && (
            <Link to={`/oferty/${o.sourceQuoteId}`} className="text-xs text-brand-600 hover:underline">
              z oferty ↗
            </Link>
          )}
        </div>

        {doZamowienia.length > 0 && (
          <button
            onClick={() => void orderFromSuppliers()}
            disabled={busy}
            className="text-sm bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700 disabled:opacity-50 font-medium shrink-0"
            title="Grupuje maszyny wg producenta i tworzy osobne zamówienie do każdego"
          >
            {busy ? 'Tworzę…' : `Zamów u producentów (${doZamowienia.length})`}
          </button>
        )}
      </div>

      {flash && (
        <div className="mb-4 text-sm text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-2">{flash}</div>
      )}
      {error && (
        <div role="alert" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {o.items.map((it) => (
          <div key={it.id} className={`p-4 border-b border-slate-100 last:border-0 ${it.cancelledAt ? 'opacity-50' : ''}`}>
            <div className="flex justify-between gap-4">
              <div className="min-w-0">
                <div className="font-medium text-slate-800">
                  {it.model}
                  {it.cancelledAt && <span className="ml-2 text-xs text-red-600">rezygnacja: {it.cancelReason}</span>}
                </div>
                <div className="text-xs text-slate-400">
                  {it.manufacturerName} · {it.catalogCode}
                </div>
                <div className="text-xs mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                  {it.supplierNumber ? (
                    <span className={it.supplierStatus === 'szkic' ? 'text-amber-700' : 'text-blue-700'}>
                      {it.supplierStatus === 'szkic' ? 'na szkicu zamówienia do producenta (jeszcze niewysłane):' : 'zamówione u producenta:'}{' '}
                      <Link to={`/zamowienia-producent/${it.supplierOrderId}`} className="underline">
                        {it.supplierNumber}
                      </Link>
                    </span>
                  ) : it.cancelledAt ? (
                    <span className="text-slate-400">nie zamówione</span>
                  ) : (
                    <span className="text-amber-700">jeszcze nie zamówione u producenta</span>
                  )}
                  {it.confirmedDeliveryDate && (
                    <span className="text-slate-500">
                      dostawa potwierdzona: {new Date(it.confirmedDeliveryDate).toLocaleDateString('pl-PL')}
                    </span>
                  )}
                  {!it.cancelledAt && (
                    <label className="text-slate-500 flex items-center gap-1" title="Termin obiecany klientowi — porównywany z potwierdzeniem producenta">
                      obiecany klientowi:
                      <input
                        type="date"
                        defaultValue={it.promisedDeliveryDate ?? ''}
                        onBlur={(e) => e.target.value !== (it.promisedDeliveryDate ?? '') && void setPromised(it.id, e.target.value || null)}
                        className="px-1 py-0.5 border border-slate-200 rounded text-xs"
                      />
                    </label>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0 text-sm">
                <div className="tabular font-semibold text-slate-800">{money(it.unitNetPrice)}</div>
                {!it.cancelledAt && !it.supplierOrderId && (
                  <button onClick={() => void cancelItem(it.id)} className="text-xs text-slate-400 hover:text-red-600 mt-1">
                    rezygnacja klienta
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {o.deliveryAtRisk && (
        <div className="mt-3 text-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          ⚠ Producent potwierdził termin późniejszy niż obiecany klientowi. Sprawdź terminy pozycji.
        </div>
      )}
    </div>
  )
}
